import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import { useIntl, defineMessages } from 'react-intl';
import cx from 'classnames';
import { getNavigationUrl } from 'volto-essvoltocomponents/helpers';
import { getBaseUrl, hasApiExpander } from '@plone/volto/helpers';
import config from '@plone/volto/registry';
import { getNavigation, getContent } from '@plone/volto/actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSortDown,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Desktop Navigation
 *
 * First two nav levels only. Top-level uses ARIA menubar; submenus use role="menu"
 * with arrow-key navigation (issue #110).
 */

const messages = defineMessages({
  openMenu: {
    id: 'Open menu',
    defaultMessage: 'Open menu',
  },
  closeMenu: {
    id: 'Close menu',
    defaultMessage: 'Close menu',
  },
  contact: {
    id: 'Contact',
    defaultMessage: 'Contact',
  },
  contactUs: {
    id: 'Contact us',
    defaultMessage: 'Contact us',
  },
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  searchSite: {
    id: 'Search Site',
    defaultMessage: 'Search Site',
  },
  mainNavigation: {
    id: 'Main navigation',
    defaultMessage: 'Main navigation',
  },
});

const FOCUSABLE_MENUITEM =
  '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])';

const Navigation = ({ pathname }) => {
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(null);
  const navigation = useRef(null);
  const topLevelRefs = useRef([]);
  const submenuRefs = useRef({});
  const dispatch = useDispatch();
  const intl = useIntl();
  const history = useHistory();

  const handleSearchClick = () => {
    history.push('/search');
  };
  const handleContactClick = () => {
    history.push('/contact-info');
  };

  const items = useSelector(
    (state) => state.navigation.items,
    shallowEqual,
  ).filter((item) => item.url !== '/' && item.title !== 'Home');

  const token = useSelector((state) => state.userSession.token, shallowEqual);

  const closeMenu = useCallback(() => {
    setDesktopMenuOpen(null);
  }, []);

  const openMenu = useCallback((index) => {
    setDesktopMenuOpen(index);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!navigation.current) return;
      if (navigation.current.contains(e.target)) return;
      closeMenu();
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [closeMenu]);

  useEffect(() => {
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), config.settings.navDepth));
    }
  }, [pathname, token, dispatch]);

  const isActive = (url) => {
    if (url === '' && pathname === '/') return true;
    if (url !== '' && pathname === url) return true;

    const pathParts = pathname.split('/').filter(Boolean);
    const urlParts = url.split('/').filter(Boolean);

    return pathParts[0] && urlParts[0] && pathParts[0] === urlParts[0];
  };

  const handleItemClick = (item) => {
    closeMenu();
    const nextUrl = getNavigationUrl(item.url);

    if (nextUrl && nextUrl !== pathname) {
      dispatch(getContent(nextUrl));
      history.push(nextUrl);
    }
  };

  const focusTopLevel = (index) => {
    const el = topLevelRefs.current[index];
    if (el) el.focus();
  };

  const focusSubmenuItem = (menuIndex, itemIndex) => {
    const menu = submenuRefs.current[menuIndex];
    if (!menu) return;
    const itemsInMenu = menu.querySelectorAll(FOCUSABLE_MENUITEM);
    if (!itemsInMenu.length) return;
    const target =
      itemIndex < 0
        ? itemsInMenu.length - 1
        : Math.max(0, Math.min(itemIndex, itemsInMenu.length - 1));
    itemsInMenu[target].focus();
  };

  const topLevelCount = items.length + 2; /* nav items + Contact + Search */

  const handleTopLevelKeyDown = (e, index, hasChildren) => {
    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const next = (index + 1) % topLevelCount;
        focusTopLevel(next);
        if (desktopMenuOpen !== null) {
          const nextHasChildren =
            next < items.length && items[next]?.items?.length > 0;
          if (nextHasChildren) openMenu(next);
          else closeMenu();
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const prev = (index - 1 + topLevelCount) % topLevelCount;
        focusTopLevel(prev);
        if (desktopMenuOpen !== null) {
          const prevHasChildren =
            prev < items.length && items[prev]?.items?.length > 0;
          if (prevHasChildren) openMenu(prev);
          else closeMenu();
        }
        break;
      }
      case 'ArrowDown': {
        if (!hasChildren) break;
        e.preventDefault();
        openMenu(index);
        // Focus first submenu item after open
        requestAnimationFrame(() => focusSubmenuItem(index, 0));
        break;
      }
      case 'ArrowUp': {
        if (!hasChildren) break;
        e.preventDefault();
        openMenu(index);
        requestAnimationFrame(() => focusSubmenuItem(index, -1));
        break;
      }
      case 'Escape': {
        if (desktopMenuOpen !== null) {
          e.preventDefault();
          closeMenu();
          focusTopLevel(index);
        }
        break;
      }
      case 'Home': {
        e.preventDefault();
        focusTopLevel(0);
        break;
      }
      case 'End': {
        e.preventDefault();
        focusTopLevel(topLevelCount - 1);
        break;
      }
      default:
        break;
    }
  };

  const handleSubmenuKeyDown = (e, menuIndex) => {
    const menu = submenuRefs.current[menuIndex];
    if (!menu) return;
    const itemsInMenu = Array.from(menu.querySelectorAll(FOCUSABLE_MENUITEM));
    const currentIndex = itemsInMenu.indexOf(document.activeElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = (currentIndex + 1) % itemsInMenu.length;
        itemsInMenu[next]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev =
          (currentIndex - 1 + itemsInMenu.length) % itemsInMenu.length;
        itemsInMenu[prev]?.focus();
        break;
      }
      case 'ArrowLeft':
      case 'Escape': {
        e.preventDefault();
        closeMenu();
        focusTopLevel(menuIndex);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        const nextTop = (menuIndex + 1) % topLevelCount;
        focusTopLevel(nextTop);
        const nextHasChildren =
          nextTop < items.length && items[nextTop]?.items?.length > 0;
        if (nextHasChildren) {
          openMenu(nextTop);
          requestAnimationFrame(() => focusSubmenuItem(nextTop, 0));
        } else {
          closeMenu();
        }
        break;
      }
      case 'Home': {
        e.preventDefault();
        itemsInMenu[0]?.focus();
        break;
      }
      case 'End': {
        e.preventDefault();
        itemsInMenu[itemsInMenu.length - 1]?.focus();
        break;
      }
      case 'Tab': {
        if (e.shiftKey) {
          // Shift + Tab on the first submenu item -> Close menu & focus current top-level button
          if (currentIndex === 0) {
            e.preventDefault();
            closeMenu();
            focusTopLevel(menuIndex);
          }
        } else {
          // Tab on the last submenu item -> Close menu & focus next top-level button
          if (currentIndex === itemsInMenu.length - 1) {
            e.preventDefault();
            closeMenu();
            const nextTop = (menuIndex + 1) % topLevelCount;
            focusTopLevel(nextTop);
          }
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <nav
      id="navigation"
      aria-label={intl.formatMessage(messages.mainNavigation)}
      className="navigation"
      ref={navigation}
    >
      <div className="computer large screen widescreen only">
        <ul className="desktop-menu" role="menubar">
          {items.map((item, index) => {
            const hasChildren = item.items && item.items.length > 0;
            const isOpen = desktopMenuOpen === index;
            const submenuId = `nav-submenu-${index}`;
            const openLabel = intl.formatMessage(messages.openMenu);
            const closeLabel = intl.formatMessage(messages.closeMenu);

            return (
              <li
                className="desktop-menu-item"
                key={item.url || index}
                role="none"
              >
                <button
                  ref={(el) => {
                    topLevelRefs.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  tabIndex={0}
                  onClick={() => {
                    if (hasChildren) {
                      if (isOpen) closeMenu();
                      else openMenu(index);
                    } else {
                      handleItemClick(item);
                    }
                  }}
                  onKeyDown={(e) =>
                    handleTopLevelKeyDown(e, index, hasChildren)
                  }
                  className={cx('item', {
                    active: isOpen,
                    current: isActive(item.url),
                  })}
                  aria-label={
                    hasChildren
                      ? `${item.title}, ${isOpen ? closeLabel : openLabel}`
                      : item.title
                  }
                  aria-haspopup={hasChildren ? 'menu' : undefined}
                  aria-expanded={hasChildren ? isOpen : undefined}
                  aria-controls={hasChildren ? submenuId : undefined}
                >
                  {item.title}
                  {hasChildren && (
                    <FontAwesomeIcon
                      icon={faSortDown}
                      size="sm"
                      className="submenu-icon"
                      aria-hidden="true"
                    />
                  )}
                </button>

                {hasChildren && (
                  <div
                    id={submenuId}
                    className={cx('submenu', { active: isOpen })}
                    hidden={!isOpen}
                  >
                    <div className="submenu-inner">
                      <ul
                        role="menu"
                        aria-label={item.title}
                        ref={(el) => {
                          submenuRefs.current[index] = el;
                        }}
                        onKeyDown={(e) => handleSubmenuKeyDown(e, index)}
                      >
                        {item.items.map((subitem) => (
                          <li
                            className="subitem-wrapper"
                            key={subitem.url}
                            role="none"
                          >
                            <NavLink
                              role="menuitem"
                              tabIndex={isOpen ? 0 : -1}
                              to={getNavigationUrl(subitem.url)}
                              onClick={() => {
                                dispatch(
                                  getContent(getNavigationUrl(subitem.url)),
                                );
                                closeMenu();
                              }}
                              className={cx({
                                current: isActive(subitem.url),
                              })}
                            >
                              {subitem.nav_title || subitem.title}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            );
          })}

          <li className="desktop-menu-item" key="contact" role="none">
            <button
              ref={(el) => {
                topLevelRefs.current[items.length] = el;
              }}
              type="button"
              role="menuitem"
              tabIndex={0}
              className="item"
              aria-label={intl.formatMessage(messages.contact)}
              title={intl.formatMessage(messages.contactUs)}
              onClick={handleContactClick}
              onKeyDown={(e) => handleTopLevelKeyDown(e, items.length, false)}
            >
              Contact
            </button>
          </li>

          <li className="desktop-menu-item" key="search" role="none">
            <button
              ref={(el) => {
                topLevelRefs.current[items.length + 1] = el;
              }}
              type="button"
              role="menuitem"
              tabIndex={0}
              className="item"
              aria-label={intl.formatMessage(messages.search)}
              title={intl.formatMessage(messages.searchSite)}
              onClick={handleSearchClick}
              onKeyDown={(e) =>
                handleTopLevelKeyDown(e, items.length + 1, false)
              }
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

Navigation.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default Navigation;
